import React, {Component} from 'react';
import {connect} from 'react-redux';
import {isEmpty, isEqual} from 'lodash';
import PropTypes from 'prop-types';
import {withRouter} from 'react-router-dom';
import cx from 'classnames';
import memoize from 'lodash/memoize';
import moment from 'moment';
import {selectFeatureFlag} from '../../../selectors/featureFlagSelectors';
import {Button, GAProvider, getHookId, ToastProvider, Icon, Radio, RadioGroup, Tooltip} from '@perion-undertone/ut-react-common';
import Toaster from '../../Toaster';
import AnalyticsLoader from './AnalyticsLoader';
import ReportsCalendar from './ReportsCalendar';
import ReportsMenu from './ReportsMenu';
import ScheduleInfo from './scheduleReport/ScheduleInfo';
import ScheduleRecurrence from './scheduleReport/ScheduleRecurrence';
import getActiveReportsList from './getActiveReportsList';
// import SQLReport from './SQLReport';
import {
    appStates, formatString, getPredefinedReportsList, setPredefinedReportPeriod,
    getPredefinedReportData, getReportPreview, setReportScheduling, PERIOD_TYPES,
    textProvider, calendarPresets, getReplacedValue, WebPreview, LoadingBar, RetryFetchData,
    getPublisherReportsConfig
} from './imports';

const CLASS_NAMES = {
    ANALYTICS_WRAPPER: 'analytics-wrapper',
    ANALYTICS_BODY_CONTAINER: 'analytics-body-container',
    ANALYTICS_OUTPUT_FORMAT_CONTAINER: 'analytics-output-format-container',
    ANALYTICS_OUTPUT_FORMAT_TITLE: 'analytics-output-format-title',
    ANALYTICS_SIDEBAR: 'analytics-sidebar',
    ANALYTICS_MAIN: 'analytics-main',
    RECURRENCE_BTN: 'recurrence-btn',
    ANALYTICS_MAIN_BODY: 'analytics-main-body',
    ANALYTICS_MAIN_FOOTER: 'analytics-main-footer',
    ANALYTICS_DATA_LOADER_DOWNLOADING: 'analytics-data-loader-downloading',
    EXPORT_BTN: 'export-btn',
    HIDDEN: 'hidden',
    ANALYTICS_LEFT_CONTAINER: 'analytics-left-container',
    ANALYTICS_RIGHT_CONTAINER: 'analytics-right-container',
    ANALYTICS_GROUPED_BY: 'analytics-grouped-by',
    ANALYTICS_GROUPED_BY_LABEL: 'analytics-grouped-by-label',
    ANALYTICS_GROUPED_BY_TEXT: 'analytics-grouped-by-text',
};

const CONSTANT_TEXTS = {
    RECURRENCE: textProvider.getText('analytics', 'recurrence'),
    PREDEFINED_REPORTS: textProvider.getText('analytics', 'predefinedReports'),
    NEW_REPORTS: textProvider.getText('analytics', 'newReports'),
    EXPORT: textProvider.getText('analytics', 'export'),
    PREVIEW: textProvider.getText('analytics', 'preview'),
    WEB: textProvider.getText('exportOptions', 'web'),
    CSV: textProvider.getText('exportOptions', 'csv'),
    EXCEL: textProvider.getText('exportOptions', 'excel'),
    DOWNLOADING_LABEL: textProvider.getText('fileUpload', 'downloading'),
    PREDEFINE_REPORT_FORMAT: textProvider.getText('ga', 'actions.predefineReportFormat'),
    REPORT_SELECTED_DATE_FORMAT: textProvider.getText('ga', 'actions.reportSelectedDate'),
    SELECT_OUTPUT_FORMAT: textProvider.getText('ga', 'actions.selectOutputFormat'),
    ANALYTICS_MODULE: textProvider.getText('ga', 'category.analyticsModule'),
    ANALYTICS_MODULE_FORMAT: textProvider.getText('ga', 'category.analyticsModuleFormat'),
    DOWNLOAD_LINK_NOTIFICATION: textProvider.getText('analytics', 'downloadLinkNotification'),
    GROUPED_BY: textProvider.getText('analytics', 'groupedBy'),
    DAYS: textProvider.getText('analytics', 'days'),
    MONTHS: textProvider.getText('analytics', 'months'),
    GROUPED_BY_TOOLTIP_TXT: textProvider.getText('analytics', 'groupedByTooltipTxt'),
};

const getMainBodyHeight = memoize((hasRecurrence) => {
    const fixedHeight = hasRecurrence ? '38px' : '0px';
    return {height: `calc(100% - ${fixedHeight})`};
});

const isSameCalendarRange = (period, from, to) =>
    period &&
    period.from &&
    period.to &&
    from &&
    to &&
    moment(period.from).isSame(from, 'day') &&
    moment(period.to).isSame(to, 'day');

class Analytics extends Component {
    constructor(props) {
        super(props);

        const {predefinedReports} = props;
        const exportType = (predefinedReports && predefinedReports.predefinedReportData && predefinedReports.predefinedReportData.exportType) || '';
        this.state = {
            isInDownloadingState: false,
            activeItem: {},
            activeItemName: 'Placement Report',
            activePreset: PERIOD_TYPES.CUSTOM,
            exportType,
            reportId: 'placement-report',
            isRecurrenceOpen: false,
            dateGranularity: 'po_dates'
        }
    }

    componentDidMount() {
        const {getPredefinedReportsList, publisherId, setPredefinedReportPeriod, getPublisherReportsConfig} = this.props;
        const defaultPreset = calendarPresets.find(preset => preset.period === PERIOD_TYPES.LAST_MONTH);
        const defaultPeriod = {
            type: defaultPreset.period,
            from: defaultPreset.startDate,
            to: defaultPreset.endDate
        };
        getPredefinedReportsList(publisherId);
        setPredefinedReportPeriod(defaultPeriod);
        getPublisherReportsConfig(publisherId);

        this.setState({activePreset: defaultPeriod.type});
    }

    componentDidUpdate(prevProps) {
        const prevDownload = prevProps.predefinedReportData && prevProps.predefinedReportData.data;
        if (prevDownload) {
            return;
        }

        const {activeItem, activeItemName} = this.state;
        const {predefinedReportsList} = this.props;

        if (predefinedReportsList && predefinedReportsList.data) {
            const newActiveItem = predefinedReportsList.data.find(item => item.name === activeItemName);
            if (!newActiveItem) {
                return;
            }
            if (newActiveItem === this.state.activeItem) {
                return;
            }
            if (isEmpty(activeItem) || !isEqual(newActiveItem, this.state.activeItem)) {
                this.setState({activeItem: newActiveItem, activeItemName: newActiveItem.name});
            }
        }
    }

    handleItemChange = (item) => {
        const {user} = this.props;

        if (item.name !== this.state.activeItemName) {
            GAProvider.notifyEvent(CONSTANT_TEXTS.ANALYTICS_MODULE, formatString(CONSTANT_TEXTS.PREDEFINE_REPORT_FORMAT, item.name), getReplacedValue(user.data.name))
        }
        this.setState({activeItem: item, activeItemName: item.name, reportId: item.id})
    }
    showEmailDownloadSuccessToaster = () => {
        return ToastProvider.success(
            <Toaster notificationIcon='icon-mail'
                     notificationHookId='toaster-success-icon'
                     notificationMessage={CONSTANT_TEXTS.DOWNLOAD_LINK_NOTIFICATION}
            />,
        true);
    }
    wait = ms => new Promise(resolve => setTimeout(resolve, ms));
    checkFileRequestTime = async() => {
        await this.wait(40000);
        if (this.props.predefinedReportData.state === 'Loading') {
            this.setState({isInDownloadingState: false})
            return this.showEmailDownloadSuccessToaster();
        }
        return false;
    };
    onExportClick = (value, reportQuery) => {
        const {user, getPredefinedReportData, publisherId, period, publisherReportsConfig} = this.props;
        const {activeItemName, reportId, exportType, dateGranularity} = this.state;
        const query = reportQuery || null;
        let isDownloading = true;
        this.setState({isInDownloadingState: true})
        if (exportType !== value) {
            GAProvider.notifyEvent(
                formatString(CONSTANT_TEXTS.ANALYTICS_MODULE_FORMAT, activeItemName),
                formatString(CONSTANT_TEXTS.SELECT_OUTPUT_FORMAT, value),
                getReplacedValue(user.data.name)
            );
        }
        this.setState({exportType: value});
        const configData = publisherReportsConfig.data;
        if(configData && configData.publisherId) {
            const reportIdsArr = configData.reportIds.split(',');
            if(reportIdsArr.includes(reportId.toString())) {
                isDownloading = false;
                this.setState({isInDownloadingState: false});
                this.showEmailDownloadSuccessToaster();
            }
        }else {
            this.checkFileRequestTime();
        }
        getPredefinedReportData(publisherId, reportId, value, period, isDownloading, user.data.email, query, dateGranularity);

    }

    onWebPreviewClick = () => {
        const {getReportPreview, publisherId, period} = this.props;
        const {reportId, dateGranularity} = this.state;
        getReportPreview(publisherId, reportId, period, dateGranularity);
    }

    openRecurrenceModal = () => {
        this.setState({isRecurrenceOpen: true});
    };

    closeRecurrenceModal = () => {
        this.setState({isRecurrenceOpen: false});
    };

    onPresetClick = (type, startDate, endDate, updateViewDateForPreset) => {
        const {setPredefinedReportPeriod, user, period: periodFromStore} = this.props;
        const {activeItemName, activePreset} = this.state;
        const period = {
            type,
            from: startDate,
            to: endDate
        };
        if (type !== activePreset) {
            GAProvider.notifyEvent(formatString(CONSTANT_TEXTS.ANALYTICS_MODULE_FORMAT, activeItemName),
                formatString(CONSTANT_TEXTS.REPORT_SELECTED_DATE_FORMAT, type), getReplacedValue(user.data.name))
        }
        this.setState({activePreset: type});
        updateViewDateForPreset([startDate, endDate]);
        if (!periodFromStore || !isSameCalendarRange(periodFromStore, startDate, endDate) || periodFromStore.type !== type) {
            setPredefinedReportPeriod(period);
        }
    }

    onCustomPeriodChange = (name, value) => {
        const {setPredefinedReportPeriod, period} = this.props;
        if (!value || !value[0] || !value[1]) {
            return;
        }
        const [from, to] = value;
        if (period && period.type === 'custom' && isSameCalendarRange(period, from, to)) {
            return;
        }
        this.setState({activePreset: PERIOD_TYPES.CUSTOM});
        setPredefinedReportPeriod({
            type: 'custom',
            from,
            to
        });
    }

    // downloadFileFromBuffer(content, name) {
    //     const base64data = Buffer.from(content, 'binary');
    //     const url = window.URL.createObjectURL(new Blob([base64data], {type: 'application/json', encoding: 'UTF-8'}));
    //     const link = document.createElement('a');
    //     link.href = url;
    //     link.setAttribute('download', name);
    //     document.body.appendChild(link);
    //     link.click();
    //     link.remove();
    //     this.setState({isDownloading: false});
    // }

    onGroupByChange = (name, value) => {
        this.setState({dateGranularity: value});
    }

    render() {
        const {predefinedReportsList, period, isReportsSchedulingEnabled, publisherId, predefinedReportData} = this.props;
        const isDownloading = (predefinedReportData && predefinedReportData.isDownloading && this.state.isInDownloadingState) || false;
        const {activeItem, activeItemName} = this.state;

        if (!predefinedReportsList || !predefinedReportsList.data ||
            predefinedReportsList.state === appStates.LOADING) {
            return <AnalyticsLoader />
        }

        if (predefinedReportsList.state === appStates.ERROR) {
            return <RetryFetchData classNames='centered' />
        }

        const activeReportsList = getActiveReportsList(predefinedReportsList.data);

        return (
            <div className={CLASS_NAMES.ANALYTICS_WRAPPER}
                 {...getHookId('analytics-page')}
            >
                {isReportsSchedulingEnabled && activeItem.hasRecurrence &&
                        <ScheduleInfo item={activeItem}
                                        setReportScheduling={this.props.setReportScheduling}
                        />
                    }
                <div className={CLASS_NAMES.ANALYTICS_BODY_CONTAINER}>
                    <div className={CLASS_NAMES.ANALYTICS_SIDEBAR}>
                        <ReportsMenu activeItemName={activeItemName}
                                     title={CONSTANT_TEXTS.NEW_REPORTS}
                                     isNewReportsList={true}
                                     reportsList={activeReportsList}
                                     handleItemChange={this.handleItemChange}
                                     isReportsSchedulingEnabled={isReportsSchedulingEnabled}
                        />
                    </div>
                    <div className={CLASS_NAMES.ANALYTICS_MAIN}>
                        <div className={CLASS_NAMES.ANALYTICS_MAIN_BODY}
                             style={getMainBodyHeight(isReportsSchedulingEnabled && activeItem.hasRecurrence)}
                        >
                            <div className={CLASS_NAMES.ANALYTICS_LEFT_CONTAINER}>
                                {period && <ReportsCalendar onPresetClick={this.onPresetClick}
                                                            onCustomPeriodChange={this.onCustomPeriodChange}
                                                            period={period}
                                                            activePreset={this.state.activePreset}
                                />}
                                <div className={CLASS_NAMES.ANALYTICS_GROUPED_BY}>
                                    <div className={CLASS_NAMES.ANALYTICS_GROUPED_BY_LABEL}>
                                        <div className={CLASS_NAMES.ANALYTICS_GROUPED_BY_TEXT}>{CONSTANT_TEXTS.GROUPED_BY}</div>
                                        <Tooltip appendToBody
                                                placement="top"
                                                trigger={<div><Icon icon='icon-additional_details' /></div>}
                                        >
                                            {CONSTANT_TEXTS.GROUPED_BY_TOOLTIP_TXT}
                                        </Tooltip>
                                    </div>
                                    <RadioGroup hookId={`reports-groupedby-${CONSTANT_TEXTS.DAYS}-radio`}
                                                name="groupBy"
                                                value={this.state.dateGranularity}
                                                onChange={this.onGroupByChange}
                                    >
                                        <Radio as='div' value='po_dates'>
                                            {CONSTANT_TEXTS.DAYS}
                                        </Radio>
                                        <Radio as='div' value='monthly'>
                                            {CONSTANT_TEXTS.MONTHS}
                                        </Radio>
                                    </RadioGroup>
                                </div>
                                <div className={CLASS_NAMES.ANALYTICS_OUTPUT_FORMAT_CONTAINER}>
                                    <div className={CLASS_NAMES.ANALYTICS_OUTPUT_FORMAT_TITLE}>
                                        {CONSTANT_TEXTS.EXPORT}
                                    </div>
                                    <Button onClick={this.onWebPreviewClick}
                                            className={CLASS_NAMES.EXPORT_BTN}
                                            hookId='preview-report-btn'
                                            icon="icon-web"
                                            iconPosition="left"
                                            transparent
                                    >
                                        {CONSTANT_TEXTS.WEB}
                                    </Button>
                                    <Button onClick={() => this.onExportClick('xlsx')}
                                            className={CLASS_NAMES.EXPORT_BTN}
                                            hookId='export-excel-report-btn'
                                            icon="icon-excel"
                                            iconPosition="left"
                                            transparent
                                    >
                                        {CONSTANT_TEXTS.EXCEL}
                                    </Button>
                                    <Button onClick={() => this.onExportClick('csv')}
                                            className={CLASS_NAMES.EXPORT_BTN}
                                            hookId='export-csv-report-btn'
                                            icon="icon-csv"
                                            iconPosition="left"
                                            transparent
                                    >
                                        {CONSTANT_TEXTS.CSV}
                                    </Button>
                                </div>
                            </div>
                            {/* {isReportSQLEditorEnabled && ( */}
                                <div className={CLASS_NAMES.ANALYTICS_RIGHT_CONTAINER}>
                                    {isReportsSchedulingEnabled &&
                                        <Button className={CLASS_NAMES.RECURRENCE_BTN}
                                                onClick={this.openRecurrenceModal}
                                                hookId="recurrence-modal-button"
                                        >
                                            {CONSTANT_TEXTS.RECURRENCE}
                                        </Button>
                                    }
                                {/* <SQLReport
                                    predefinedReportsList={predefinedReportsList}
                                    reportId={reportId}
                                    publisherId={publisherId}
                                    startPreviewQuery={this.onExportClick}
                                ></SQLReport> */}
                            </div>
                            {/* )} */}
                        </div>
                        <div className={CLASS_NAMES.ANALYTICS_MAIN_FOOTER}>
                            <span className={cx({[CLASS_NAMES.HIDDEN]: !isDownloading},
                                CLASS_NAMES.ANALYTICS_DATA_LOADER_DOWNLOADING)}
                            >
                                {CONSTANT_TEXTS.DOWNLOADING_LABEL}
                            </span>
                            <LoadingBar isLoading={isDownloading} />
                        </div>
                    </div>
                </div>
                <WebPreview onExportClick={this.onExportClick} />
                {isReportsSchedulingEnabled && activeItem &&
                    <ScheduleRecurrence item={activeItem}
                                        publisherId={publisherId}
                                        isOpen={this.state.isRecurrenceOpen}
                                        closeRecurrenceModal={this.closeRecurrenceModal}
                                        setReportScheduling={this.props.setReportScheduling}
                    />
                }
            </div>
        );
    }
}
Analytics.propTypes = {
    getReportPreview: PropTypes.func,
    getPredefinedReportsList: PropTypes.func,
    getPredefinedReportData: PropTypes.func,
    setPredefinedReportPeriod: PropTypes.func,
    setReportScheduling: PropTypes.func,
    predefinedReportsList: PropTypes.object,
    predefinedReportData: PropTypes.object,
    period: PropTypes.object,
    isReportsSchedulingEnabled: PropTypes.bool,
    publisherId: PropTypes.number,
    user: PropTypes.object,
    isDownloading: PropTypes.bool,
    predefinedReports: PropTypes.object,
    getPublisherReportsConfig: PropTypes.func,
    publisherReportsConfig: PropTypes.object,
    // isReportSQLEditorEnabled: PropTypes.bool,
};

const mapStateToProps = (state) => ({
    publisherId: state.app.publisher.data.id,
    predefinedReportsList: state.predefinedReports.predefinedReportsList,
    period: state.predefinedReports.period,
    predefinedReportData: state.predefinedReports.predefinedReportData,
    user: state.app.user,
    isReportsSchedulingEnabled: selectFeatureFlag('reportsScheduling')(state),
    // isReportSQLEditorEnabled: selectFeatureFlag('sqlReportEditor')(state),
    publisherReportsConfig: state.predefinedReports.publisherReportsConfig
});
const mapDispatchToProps = ({
    getReportPreview,
    getPredefinedReportsList,
    getPredefinedReportData,
    setPredefinedReportPeriod,
    setReportScheduling,
    getPublisherReportsConfig
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Analytics));
